import SystemDialogService from '../../../../shared/services/systemDialogService.js';
import { WidgetType, WidgetTypeMeta } from './widgets/widgetTypes.js';
import { WidgetRegistry } from './widgets/registry.js';
import dashboardService from '../services/dashboardService.js';

const DEFAULT_LAYOUT = [
    { type: WidgetType.EigeneTodos },
    { type: WidgetType.OffeneUrlaubsantraege },
];

/**
 * Dashboard Manager: rendert das Widget-Grid auf dem Home Screen, verwaltet
 * Hinzufügen/Entfernen/Neuanordnen von Widget-Instanzen und persistiert die
 * Konfiguration im localStorage.
 */
class Dashboard {
    constructor() {
        this.widgets = []; // [{ type, instanceId, instance, element }]
        this.container = null;
        this.gridEl = null;
    }

    saveConfig() {
        const config = this.widgets.map((w) => {
            const widgetState = w.instance.serializeState?.() || {};
            // rowSpan gehört zum Framework-State (vom Resize-Handle gesetzt), nicht
            // zu den Widget-spezifischen Feldern. Hier zusammenführen, ohne dass
            // jedes Widget seine eigene serializeState() anpassen muss.
            return {
                type: w.type,
                instanceId: w.instanceId,
                state: { ...widgetState, rowSpan: w.instance.getRowSpan() },
            };
        });
        dashboardService.setLayout(config);
    }

    makeId() {
        return `w_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
    }

    /**
     * Mountet das Dashboard in den übergebenen Container.
     */
    async render(container) {
        this.disposeWidgets();
        this.container = container;
        container.innerHTML = '<div class="dashboard"></div>';
        this.gridEl = container.querySelector('.dashboard');

        const data = await dashboardService.lese();
        const hasSavedLayout = Array.isArray(data?.Layout) && data.Layout.length > 0;
        const config = hasSavedLayout ? data.Layout : DEFAULT_LAYOUT;
        // Add-Tile zuerst rendern, damit es gleichzeitig mit den Widget-Shells sichtbar wird.
        // addWidget fügt neue Widgets immer per insertBefore vor das Add-Tile ein.
        this.renderAddTile();
        // Tile-Shells und Render parallel anstoßen, damit Daten gleichzeitig geladen
        // werden statt sequenziell – sonst erscheinen Widgets nacheinander wie ein Wasserfall.
        await Promise.all(
            config
                .filter((item) => WidgetRegistry[item.type])
                .map((item) => this.addWidget(item.type, {
                    instanceId: item.instanceId,
                    state: item.state,
                    persist: false,
                })),
        );
        if (!hasSavedLayout) {
            this.saveConfig();
        }
    }

    disposeWidgets() {
        this.widgets.forEach((w) => w.instance.destroy?.());
        this.widgets = [];
    }

    /**
     * Erstellt eine neue Widget-Instanz, hängt sie als Tile ans Grid und ruft anschließend
     * deren render() auf.
     *
     * @param {string} type - Wert aus WidgetType (z.B. 'eigeneTodos', 'notizblock').
     * @param {object} [options]
     * @param {string} [options.instanceId] - Wird nur beim Wiederherstellen aus dem
     *   gespeicherten Layout übergeben, damit Daten wie Notiz-Texte oder Widget-State
     *   wieder zur richtigen Tile gehören. Bei neu erstellten Widgets generieren wir
     *   eine frische ID per makeId().
     * @param {object} [options.state] - Persistierter Widget-State aus Layout[].state
     *   (z.B. Notepad-Farbe oder ausgewählter Filterchip). Wird an den Widget-Konstruktor
     *   weitergereicht.
     * @param {boolean} [options.persist=true] - false beim initialen Hydratisieren,
     *   damit wir nicht für jedes geladene Widget einen Save Roundtrip ausführen.
     */
    async addWidget(type, options = {}) {
        // WidgetRegistry mappt Widget-Typ-Strings auf ihre Klassen, z.B.
        //   { 'notizblock': NotepadWidget, 'eigeneTodos': OwnTodoWidget, ... }
        // (siehe widgets/registry.js). Wir holen die zum type passende Klasse heraus,
        // damit wir sie gleich mit `new` instanziieren können. Großschreibung folgt der
        // JS-Konvention für Klassen/Konstruktoren.
        const WidgetClass = WidgetRegistry[type];
        if (!WidgetClass) return; // Unbekannter Typ – ignorieren statt zu crashen.
        const instanceId = options.instanceId || this.makeId();
        // `new WidgetClass(...)` ruft den Konstruktor der jeweiligen Widget-Klasse auf.
        // Alle Widgets erben von BaseWidget und nehmen (type, instanceId, state) entgegen.
        const instance = new WidgetClass(type, instanceId, options.state || {});
        const tile = this.createTile(instance);
        const addTile = this.gridEl.querySelector('.dashboard-add-widget');
        if (addTile) {
            this.gridEl.insertBefore(tile, addTile);
        } else {
            this.gridEl.appendChild(tile);
        }
        this.widgets.push({ type, instanceId, instance, element: tile });
        try {
            await instance.render(tile.querySelector('.dashboard-widget-body'));
        } catch (err) {
            console.error('Widget render failed:', err);
            tile.querySelector('.dashboard-widget-body').innerHTML =
                '<div class="widget-empty">Fehler beim Laden des Widgets.</div>';
        }
        if (options.persist !== false) {
            this.saveConfig();
        }
    }

    async removeWidget(instanceId) {
        const idx = this.widgets.findIndex((w) => w.instanceId === instanceId);
        if (idx < 0) return;
        const w = this.widgets[idx];
        // Widgets mit Nutzerdaten zeigen vor dem Entfernen eine Warnung an.
        const warning = w.instance.getRemovalWarning?.();
        if (warning) {
            const messageEl = document.getElementById('dashboard-remove-widget-message');
            if (messageEl) messageEl.innerText = warning;
            const result = await SystemDialogService.instance.displayAsync('dashboard-remove-widget-dialog');
            if (!result?.success) return;
        }
        await w.instance.onRemove?.();
        w.instance.destroy?.();
        w.element.remove();
        this.widgets.splice(idx, 1);
        this.saveConfig();
    }

    createTile(widgetInstance) {
        const tpl = document.querySelector('[dashboard-widget-template]');
        const tile = tpl.content.cloneNode(true).children[0];
        tile.dataset.instanceId = widgetInstance.instanceId;
        tile.dataset.type = widgetInstance.type;
        tile.querySelector('.widget-name').innerText = widgetInstance.getTitle();
        tile.querySelector('.widget-icon').classList.add(widgetInstance.getIcon());
        if (widgetInstance.getRowSpan?.() === 2) {
            tile.classList.add('dashboard-widget-tall');
        }
        this.attachResizeHandle(tile, widgetInstance);
        tile.querySelector('.widget-remove').addEventListener('click', () => {
            this.removeWidget(widgetInstance.instanceId);
        });
        tile.querySelector('.widget-refresh').addEventListener('click', () => {
            widgetInstance.refresh();
        });
        this.attachDragHandlers(tile);
        return tile;
    }

    /**
     * Fügt einen Resize-Griff am unteren Rand eines Widgets ein. Per Drag nach unten
     * wird das Widget 2-zeilig, nach oben 1-zeilig. Snap-Schwelle = halbe Zeilenhöhe.
     */
    attachResizeHandle(tile, widget) {
        const handle = document.createElement('div');
        handle.className = 'dashboard-widget-resize';
        handle.title = 'Widgetgröße anpassen (nach oben/unten ziehen)';
        // Wichtig: das umgebende Tile ist draggable="true" (HTML5 DnD). Ein Mousedown
        // auf dem Griff würde sonst sofort den Tile-Drag starten und unser Pointer-Resize
        // unterbinden. draggable=false + dragstart-Cancel verhindert das zuverlässig.
        handle.setAttribute('draggable', 'false');
        handle.addEventListener('dragstart', (e) => {
            e.preventDefault();
            e.stopPropagation();
        });
        handle.addEventListener('mousedown', (e) => e.stopPropagation());
        handle.addEventListener('touchstart', (e) => e.stopPropagation(), { passive: true });
        tile.appendChild(handle);

        let startY = 0;
        let startSpan = 1;
        let activePointerId = null;

        const stop = () => {
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
            window.removeEventListener('pointercancel', onUp);
            if (activePointerId !== null) {
                try { handle.releasePointerCapture(activePointerId); } catch {}
            }
            activePointerId = null;
            tile.classList.remove('resizing');
            tile._resizing = false;
        };

        const onMove = (e) => {
            if (e.pointerId !== activePointerId) return;
            const dy = e.clientY - startY;
            const rowHeight = tile.offsetHeight / startSpan;
            const threshold = rowHeight * 0.4;
            let newSpan = startSpan;
            if (startSpan === 1 && dy > threshold) newSpan = 2;
            else if (startSpan === 2 && dy < -threshold) newSpan = 1;
            if (newSpan !== widget.getRowSpan()) {
                widget.setRowSpan(newSpan);
                tile.classList.toggle('dashboard-widget-tall', newSpan === 2);
            }
        };

        const onUp = (e) => {
            if (e.pointerId !== activePointerId) return;
            const initial = startSpan;
            const finalSpan = widget.getRowSpan();
            stop();
            if (initial !== finalSpan) {
                this.saveConfig();
            }
        };

        handle.addEventListener('pointerdown', (e) => {
            e.stopPropagation();
            if (e.pointerType === 'mouse' && e.button !== 0) return;
            startY = e.clientY;
            startSpan = widget.getRowSpan();
            activePointerId = e.pointerId;
            try { handle.setPointerCapture(e.pointerId); } catch {}
            window.addEventListener('pointermove', onMove);
            window.addEventListener('pointerup', onUp);
            window.addEventListener('pointercancel', onUp);
            tile.classList.add('resizing');
            // Markiert das Tile, sodass der dragstart-Handler (HTML5 DnD) den Drag abbricht,
            // wenn das Pressen auf dem Resize-Griff stattfand. Ohne das würde das ganze
            // Widget mitziehen statt der Größenanpassung.
            tile._resizing = true;
        });

        // Tile-Drag abbrechen, falls der Resize laufen soll.
        tile.addEventListener('dragstart', (e) => {
            if (tile._resizing) {
                e.preventDefault();
                e.stopPropagation();
            }
        }, true);
    }

    attachDragHandlers(tile) {
        tile.addEventListener('dragstart', (e) => {
            // Nur ziehen, wenn der Drag im Header oder einem nicht-interaktiven Bereich startet
            const fromBody = e.target.closest('.dashboard-widget-body');
            if (fromBody) {
                e.preventDefault();
                return;
            }
            e.dataTransfer.effectAllowed = 'move';
            e.dataTransfer.setData('text/plain', tile.dataset.instanceId);
            tile.classList.add('dragging');
        });
        tile.addEventListener('dragend', () => {
            tile.classList.remove('dragging');
            this.gridEl.querySelectorAll('.drop-target').forEach((el) => el.classList.remove('drop-target'));
            // dragend fires regardless of where the drop landed — save here so reorders always persist.
            this.syncOrderFromDOM();
        });
        tile.addEventListener('dragover', (e) => {
            const dragging = this.gridEl.querySelector('.dragging');
            if (!dragging || dragging === tile) return;
            e.preventDefault();
            e.dataTransfer.dropEffect = 'move';
            const rect = tile.getBoundingClientRect();
            const before = (e.clientY - rect.top) < rect.height / 2;
            if (before) {
                this.gridEl.insertBefore(dragging, tile);
            } else {
                this.gridEl.insertBefore(dragging, tile.nextSibling);
            }
        });
        tile.addEventListener('drop', (e) => {
            e.preventDefault();
        });
    }

    syncOrderFromDOM() {
        const order = Array.from(this.gridEl.querySelectorAll('.dashboard-widget'))
            .map((el) => el.dataset.instanceId);
        this.widgets.sort((a, b) => order.indexOf(a.instanceId) - order.indexOf(b.instanceId));
        this.saveConfig();
    }

    renderAddTile() {
        const addTile = document.createElement('div');
        addTile.className = 'dashboard-add-widget';
        addTile.innerHTML = '<i class="bi bi-plus-circle"></i><span>Widget hinzufügen</span>';
        addTile.addEventListener('click', () => this.openAddDialog());
        // Erlaube Drop hinter das letzte Widget durch Hover über das Add-Tile.
        addTile.addEventListener('dragover', (e) => {
            const dragging = this.gridEl.querySelector('.dragging');
            if (!dragging) return;
            e.preventDefault();
            e.dataTransfer.dropEffect = 'move';
            this.gridEl.insertBefore(dragging, addTile);
        });
        addTile.addEventListener('drop', (e) => e.preventDefault());
        this.gridEl.appendChild(addTile);
    }

    openAddDialog() {
        const list = document.getElementById('dashboard-widget-type-list');
        if (!list) return;
        list.innerHTML = '';
        Object.entries(WidgetTypeMeta).forEach(([type, meta]) => {
            const item = document.createElement('div');
            item.className = 'dashboard-widget-type-item';
            item.innerHTML = `
                <i class="bi ${meta.icon}"></i>
                <div>
                    <div class="name">${meta.name}</div>
                    <div class="desc">${meta.description}</div>
                </div>
            `;
            item.addEventListener('click', () => {
                SystemDialogService.instance.confirm(type);
            });
            list.appendChild(item);
        });
        SystemDialogService.instance.displayAsync('dashboard-add-widget-dialog').then((result) => {
            if (result.success && result.data) {
                this.addWidget(result.data);
            }
        });
    }
}

const dashboard = new Dashboard();
export default dashboard;
